import { findScanDate } from "@/components/ui/use-scan-date";
import {
  LightweightElement,
  LightweightElementsRecord,
} from "@/store/lightweight-elements-slice";
import { convertToDateString, walkWithQueue } from "@faro-lotv/foundation";
import {
  IElement,
  IElementType,
  IElementTypeHint,
} from "@faro-lotv/ielement-types";
import { ClustersRecord, IElementsRecord } from "@faro-lotv/project-source";
import {
  CaptureTreeEntity,
  CaptureTreeEntityType,
} from "@faro-lotv/service-wires";
import { findLatestReferenceDate } from "./custom-date-utils";
import { elementToTreeData, TreeData } from "./tree-data";
import { TreeGenerationData } from "./tree-filters-utils";

function computeAllowedClusters(
  dataset: LightweightElement | IElement,
  clusters: ClustersRecord,
): ClustersRecord {
  const allowedClusters: ClustersRecord = {};

  // Since we don't know which scans of dataset belong to a specific tree of clusters
  // we reconstruct the tree structure starting from the scans and walking the capture tree
  // up to the root
  const clusterQueue = [...(dataset.childrenIds ?? [])];
  walkWithQueue(clusterQueue, (id, append) => {
    const elementInCluster = clusters[id];
    if (!elementInCluster) return;

    const parent = elementInCluster.parentId
      ? clusters[elementInCluster.parentId]
      : null;
    if (!parent) {
      return;
    }

    append(parent.id);

    if (parent.type !== CaptureTreeEntityType.cluster) return;

    allowedClusters[parent.id] = parent;
  });
  return allowedClusters;
}

type ClusterTreeElement = CaptureTreeEntity & Pick<IElement, "childrenIds">;

type GenerateClusterTreeParams = {
  /** The current cluster element */
  clusterElement: ClusterTreeElement;
  /** All the available clusters in the project */
  clusters: ClustersRecord;
  /** Dataset whose clusters should be added to the tree */
  dataset: LightweightElement | IElement;
  /** All the elements in the project */
  elements: LightweightElementsRecord | IElementsRecord;
  /* Optional clusters that are allowed to be shown in the tree. */
  allowedClusters?: ClustersRecord;
};

/**
 * @returns the tree representation of the clusters for the given dataset
 */
export function generateClusterTree({
  clusterElement,
  clusters,
  dataset,
  elements,
  allowedClusters,
}: GenerateClusterTreeParams): TreeData[] {
  // Ignore the clusters structure for SiteScape datasets
  if (clusterElement.type === CaptureTreeEntityType.siteScapeScan) {
    return [];
  }

  // Calculate allowed clusters only on the first call (when allowedClusters is not provided)
  if (!allowedClusters) {
    allowedClusters = computeAllowedClusters(dataset, clusters);
  }

  const children: TreeData[] = [];
  for (const childId of clusterElement.childrenIds ?? []) {
    const cluster = clusters[childId];
    if (
      !cluster ||
      (cluster.type === CaptureTreeEntityType.cluster &&
        !allowedClusters[childId])
    ) {
      continue;
    }

    children.push(
      ...generateClusterTree({
        clusterElement: cluster,
        clusters,
        dataset,
        elements,
        allowedClusters,
      }),
    );
  }

  // Do not show the root element as a child of the dataset
  if (clusterElement.type === CaptureTreeEntityType.root) {
    return children;
  }

  // Use element from the IElement tree to get the labels from for filtering
  const projectElement = elements[clusterElement.id];

  // Multiple datasets in the capture may have a common ancestor cluster,
  // we need to be sure to define a unique id for every cluster
  // to not break the interaction with the project tree
  if (clusterElement.type === CaptureTreeEntityType.cluster) {
    return [
      elementToTreeData({
        element: clusterElement,
        id: `${dataset.id}-${clusterElement.id}`,
        type: IElementType.section,
        typeHint: clusterElement.type,
        children,
        labelIds: projectElement?.labelIds ?? [],
      }),
    ];
  }

  const createdAt = computeCreationDate(
    clusterElement,
    projectElement,
    elements,
  );

  // Create the node for the current element
  return [
    elementToTreeData({
      element: clusterElement,
      type: IElementType.section,
      typeHint: clusterElement.type,
      parentElement: dataset,
      children,
      labelIds: projectElement?.labelIds ?? [],
      createdAt,
    }),
  ];
}

/**
 * This function solves a problem in the current data structure. For scan datasets in the capture tree,
 * it can happen that the creation date of the "Section-FocusScan" element is very different from the actual
 * creation date of the underlying scan and pano. The scan should be filterable by text, tags, and date.
 * Text and tags are available in the parent section element. The correct date is only available in the child
 * img360 element. If the argument of this function is a section with an img360 as a child, the function will
 * compute the custom date of that img360 element.
 *
 * @param clusterElement The current cluster element
 * @param projectElement The corresponding project element
 * @param elements The store with all the elements
 * @returns The creation date of the given element that should be considered for filtering.
 */
function computeCreationDate(
  clusterElement: ClusterTreeElement,
  projectElement: LightweightElement | IElement | undefined,
  elements: LightweightElementsRecord | IElementsRecord,
): string {
  if (!projectElement) {
    return clusterElement.createdAt;
  }
  const { childrenIds } = projectElement;
  if (!childrenIds || childrenIds.length === 0) {
    return projectElement.createdAt;
  }

  for (const childId of childrenIds) {
    const childElement = elements[childId];
    if (!childElement) continue;

    const scanDate = findScanDate(childElement, elements);
    if (scanDate) {
      return scanDate;
    }

    if (childElement.type === IElementType.img360) {
      return childElement.createdAt;
    }
  }
  return projectElement.createdAt;
}

/**
 * Append a fake "4D Session" folder with all the datasets in an area that are part of the capture tree
 *
 * If the node is not an area or does not have dataset inside no new node is created
 *
 * @param node to check for datasets in the capture tree
 * @param elements in the project
 * @param clusters The main revision tree used to build the clusters structure
 * @param treeGenerationData loaded areas, filtering functions
 * @returns the fake "4D Session" folder
 */
export function createArea4dSessionNodeIfNeeded(
  node: LightweightElement | IElement,
  elements: LightweightElementsRecord | IElementsRecord,
  treeGenerationData: TreeGenerationData,
): TreeData[] {
  const dataSets = treeGenerationData.areaDataSets[node.id];

  // Node is not an area or it does not contain any datasets
  if (!dataSets) return [];

  const children: TreeData[] = [];

  const clusterRoot = Object.values(treeGenerationData.clusters).find(
    (cluster) => cluster?.type === CaptureTreeEntityType.root,
  );
  for (const dataSet of dataSets) {
    const dataSetElement = elements[dataSet.elementId];

    // DataSets from the capture tree are not wrapped in a data session
    // TODO: Remove when all projects will be migrated to the capture tree (https://faro01.atlassian.net/browse/SWEB-4468)
    const isFromCaptureTree =
      dataSetElement?.parentId &&
      elements[dataSetElement.parentId]?.typeHint !==
        IElementTypeHint.dataSession;

    // The dataset is not loaded yet
    if (!dataSetElement || !isFromCaptureTree) continue;

    // Skip datasets with cluster structure
    if (
      dataSetElement.childrenIds?.some(
        (id) => elements[id]?.type === IElementType.cluster,
      )
    ) {
      continue;
    }

    const datasetChildren: TreeData[] = [];
    if (
      dataSetElement.childrenIds &&
      dataSetElement.childrenIds.length > 0 &&
      clusterRoot
    ) {
      // Create the sub-tree with clusters for the parent dataset
      datasetChildren.push(
        ...generateClusterTree({
          clusterElement: clusterRoot,
          clusters: treeGenerationData.clusters,
          dataset: dataSetElement,
          elements,
        }),
      );
    }

    const customDate = findLatestReferenceDate(dataSetElement, elements);

    // If it's a dataset from the capture tree, the latest scan date is used (if available)
    // Otherwise, the dataset creation date is used
    const dateToUse = customDate ?? dataSetElement.createdAt;

    children.push(
      elementToTreeData({
        element: dataSetElement,
        label: `${convertToDateString(dateToUse)} - ${dataSetElement.name}`,
        children: datasetChildren,
        parentElement: {
          type: IElementType.section,
          typeHint: IElementTypeHint.dataSets,
        },
      }),
    );
  }

  return children;
}
